<template>
  <div class="market-compare q-mx-auto">
    <template v-if="!displayNotSubscribed">
      <q-card flat bordered>
        <div class="flex q-pa-md">
          <div class="text-h6">Regional Reports</div>
          <q-space />
          <div class="q-my-auto">
            <q-btn
              text-color="grey-7"
              no-caps
              dense
              round
              unelevated
              class="q-mr-sm"
              icon="refresh"
              size="sm"
              @click="refreshSubscriptions"
            />
          </div>
          <filter-by-owner v-model="filteredOwner" class="q-mr-sm" :all-owners="allOwners" />
          <div v-if="$q.screen.gt.sm" class="row q-gutter-sm">
            <q-input
              v-model="filter"
              style="width: 200px"
              dense
              filled
              label="Search Reports..."
              clearable
            >
              <template #prepend>
                <q-icon name="search" />
              </template>
            </q-input>
          </div>
        </div>
        <q-markup-table v-if="loading" bordered dense>
          <thead>
            <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <th scope="col" />
              <th v-for="n in 3" :key="n" scope="col">
                <q-skeleton animation="blink" type="text" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in 10" :key="n">
              <td style="width: 33%">
                <q-skeleton animation="blink" type="text" />
              </td>
              <td style="width: 23%">
                <q-skeleton animation="blink" type="text" />
              </td>
              <td style="width: 44%">
                <q-skeleton animation="blink" type="text" />
              </td>
              <td class="row justify-end" style="height: 45px">
                <q-skeleton animation="blink" type="text" style="width: 125px" />
              </td>
            </tr>
          </tbody>
        </q-markup-table>
        <q-table
          v-else
          dense
          flat
          bordered
          row-key="id"
          :rows="filteredRows"
          :columns="columns"
          :pagination="{
            rowsPerPage: 10
          }"
          :filter="filter"
          :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <template #no-data>
            <div class="q-mx-auto q-my-md text-center">
              <q-img src="/empty-state-3.svg" style="width: 100px" class="q-my-lg" />
              <div class="text-bold" style="font-size: 17px">No Regional Report?</div>
              <div class="text-grey-8" style="font-size: 15px">
                <span v-if="hasMarketReports">
                  Create a report for any market, it only takes a minute.
                </span>
                <span v-else> Subscribe to Market Reports to create a report for any market. </span>
              </div>
              <div class="q-mx-auto q-my-lg">
                <q-btn
                  color="primary"
                  no-caps
                  unelevated
                  :to="hasMarketReports ? { name: 'New Regional Report' } : undefined"
                  @click="!hasMarketReports ? (displayNotSubscribed = true) : undefined"
                >
                  <q-icon name="add" class="q-mr-sm" />New Report
                </q-btn>
              </div>
            </div>
          </template>
          <template #body-cell-name="props">
            <q-td :props="props">
              <div class="row q-gutter-x-sm">
                <router-link
                  :to="{ name: 'Regional Report', params: { reportId: props.row.id } }"
                  class="market-link"
                >
                  <div class="text-bold">{{ props.value }}</div>
                </router-link>
              </div>
            </q-td>
          </template>
          <template #body-cell-user="props">
            <q-td :props="props">
              <div v-if="props.value" class="flex">
                <q-avatar size="28px" round class="bg-grey-4">
                  <q-img
                    v-if="props.value?.picture"
                    :src="props.value.picture"
                    referrerpolicy="no-referrer"
                    no-spinner
                  />
                  <q-icon v-else name="person" />
                </q-avatar>
                <div v-if="$q.screen.gt.md" class="q-ml-sm q-my-auto">
                  {{ userToName(props.value) }}
                </div>
                <q-tooltip v-else>{{ userToName(props.value) }}</q-tooltip>
              </div>
            </q-td>
          </template>
          <template #body-cell-actions="props">
            <q-td :props="props">
              <q-btn icon="more_vert" dense flat>
                <q-menu transition-duration="100" max-width="170px" auto-close>
                  <div>
                    <q-btn
                      v-if="reportInOrg(props.row)"
                      flat
                      no-caps
                      square
                      align="left"
                      class="full-width"
                      :to="{ name: 'Edit Regional Report', params: { reportId: props.row.id } }"
                    >
                      <q-icon name="edit" size="15px" class="q-mr-md" />
                      <div>Edit</div>
                    </q-btn>
                    <q-separator />
                    <q-btn
                      flat
                      no-caps
                      square
                      align="left"
                      class="full-width"
                      @click="copyReport(props.row)"
                    >
                      <q-icon name="content_copy" size="15px" class="q-mr-md" />
                      <div>Copy</div>
                    </q-btn>
                    <q-separator />
                    <q-btn
                      v-if="reportInOrg(props.row)"
                      :disable="!allowedToAdminOrOwner(props.row.user_id)"
                      flat
                      no-caps
                      square
                      align="left"
                      class="full-width"
                      @click="validateDelete(props.row)"
                    >
                      <q-tooltip v-if="!allowedToAdminOrOwner(props.row)">
                        Only this report's owner can delete it
                      </q-tooltip>
                      <q-icon name="delete" size="15px" class="q-mr-md" />
                      <div>Delete</div>
                    </q-btn>
                  </div>
                </q-menu>
              </q-btn>
            </q-td>
          </template>
        </q-table>
      </q-card>
      <safe-teleport to="#toolbar-portal">
        <q-toolbar
          class="q-px-sm row justify-center"
          :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
          style="height: 75px"
          :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
        >
          <div class="market-compare full-width row items-center">
            <div style="font-size: 20px; font-weight: 500">
              <q-icon name="map" class="q-mr-sm" />
              Dashboard
            </div>
            <q-chip
              v-if="marketReportsTrialDays > 0 && $q.screen.gt.sm"
              :ripple="false"
              class="text-primary q-mx-md"
              outline
            >
              <div class="text-center full-width">
                Trial ends in<b class="q-ml-xs">{{ marketReportsTrialDays }} days</b>
              </div>
              <q-tooltip> Trial ends on {{ formatDate(marketReportsTrialEnd) }} </q-tooltip>
            </q-chip>
            <q-space />
            <q-btn
              color="primary"
              no-caps
              unelevated
              :to="hasMarketReports ? { name: 'New Regional Report' } : undefined"
              @click="!hasMarketReports ? (displayNotSubscribed = true) : undefined"
            >
              New Report
            </q-btn>
          </div>
        </q-toolbar>
      </safe-teleport>
    </template>
    <div v-else class="q-my-xl q-mx-auto" style="max-width: 1280px">
      <not-subscribed />
    </div>
  </div>
  <q-dialog v-if="deleteDialog" v-model="deleteDialog" transition-duration="100">
    <q-card style="width: 400px">
      <q-card-section class="row items-center">
        <span class="q-ml-sm">
          Are you sure you want to permanently delete <b>{{ toDelete?.name }}</b
          >?
        </span>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
        <q-btn
          v-close-popup
          no-caps
          unelevated
          label="Delete"
          color="red"
          :loading="deleteLoading"
          @click="deleteReport"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script lang="ts">
import type { QTableColumn } from 'quasar'
import { formatDistance, parseISO } from 'date-fns'
import { formatDate } from '@/utils'
import { useMarketReportStore } from '@/stores/marketreport'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'
import NotSubscribed from '@/components/MarketReports/NotSubscribed.vue'
import type { MarketReportSubscriptionAttributes, AccessUser, UserAttributes } from 'common'
import FilterByOwner from '@/components/FilterByOwner.vue'

export default {
  name: 'MarketReportsView',
  components: {
    FilterByOwner,
    NotSubscribed
  },
  data() {
    return {
      formatDate,
      loading: false,
      deleteDialog: false,
      toDelete: null as MarketReportSubscriptionAttributes | null,
      deleteLoading: false,
      filter: '',
      displayNotSubscribed: false,
      filteredOwner: null as AccessUser | null
    }
  },
  computed: {
    marketReportsTrialEnd(): string | null {
      const sub = useStripeStore().subscriptions.find((_) =>
        _.products.includes(import.meta.env.VITE_STRIPE_MARKET_REPORTS_PRODUCT)
      )
      return sub?.trial_end || null
    },
    marketReportsTrialDays(): number {
      return useStripeStore().marketTrialDaysLeft || 0
    },
    user() {
      return this.$auth0.user?.value
    },
    allOwners(): AccessUser[] {
      const reportUserIds = new Set(
        useMarketReportStore().activeSubscriptions.map((_) => _.user_id)
      )
      return useUserStore().peopleInOrgOrShared.filter((_) => _.id && reportUserIds.has(_.id))
    },
    filteredRows(): MarketReportSubscriptionAttributes[] {
      let result = this.rows

      // filter by owner
      const filteredOwner = this.filteredOwner
      if (filteredOwner !== null) {
        result = result.filter((report) => report.user_id && filteredOwner.id === report.user_id)
      }

      return result
    },
    rows() {
      return useMarketReportStore().activeSubscriptions
    },
    hasMarketReports(): boolean {
      return useStripeStore().hasMarketReports
    },
    columns() {
      const cols: QTableColumn[] = [
        {
          field: 'name',
          name: 'name',
          label: 'Report Name',
          sortable: true,
          align: 'left'
        },
        {
          field: (row) => this.getUser(row.user_id),
          name: 'user',
          label: 'Owner',
          sortable: true,
          sort: (a: UserAttributes, b: UserAttributes) => {
            return a.id?.localeCompare(b.id)
          },
          align: 'left'
        },
        {
          field: 'modified_on',
          format: (val: string) => {
            return formatDistance(parseISO(val), new Date(), { addSuffix: true })
          },
          name: 'modified_on',
          label: 'Last Modified',
          sortable: true,
          align: 'left'
        }
      ]

      if (this.hasMarketReports) {
        cols.push({
          name: 'actions',
          label: 'Actions',
          field: 'actions'
        })
      }
      return cols
    }
  },
  created() {
    if (!this.rows.length && this.hasMarketReports) {
      this.refreshSubscriptions()
    }
    this.displayNotSubscribed = !this.hasMarketReports
  },
  methods: {
    allowedToAdminOrOwner(recordUserId: string | undefined) {
      return useUserStore().allowedToAdmin(recordUserId, true)
    },
    getUser(id: string) {
      return useUserStore().peopleInOrgOrSharedPerId.get(id)
    },
    userToName(user?: AccessUser): string {
      if (user) {
        if (user.id === this.$auth0.user.value?.sub) {
          return 'me'
        }
        if (user.given_name && user.family_name) {
          return `${user.given_name} ${user.family_name}`
        }
        return user.email
      }
      return 'Unknown User'
    },
    refreshSubscriptions() {
      this.loading = true
      useMarketReportStore()
        .refreshList()
        .finally(() => {
          this.loading = false
        })
    },
    validateDelete(row: MarketReportSubscriptionAttributes) {
      this.deleteDialog = true
      this.toDelete = row
    },
    deleteReport() {
      const deleteId = this.toDelete?.id
      if (deleteId !== undefined) {
        this.deleteLoading = true
        useMarketReportStore()
          .delete(deleteId)
          .then(() => {
            this.deleteLoading = false
          })
      }
    },
    copyReport(row: MarketReportSubscriptionAttributes) {
      useMarketReportStore().create({
        ...row,
        name: `${row.name} (Copy)`
      })
    },
    reportInOrg(row: MarketReportSubscriptionAttributes) {
      return useUserStore().reportInOrg(row)
    }
  }
}
</script>

<style lang="scss" scoped>
.text-elipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.market-compare {
  max-width: 1600px;
}
.market-link {
  text-decoration: none;
  color: inherit;
  font-weight: 500;

  &:hover {
    color: $primary;
  }
}
</style>
